import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Mail, CheckCircle2 } from 'lucide-react';
import company from '../data/company';

const ContactForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    companyName: '',
    country: '',
    email: '',
    phone: '',
    buyerType: 'International Buyer',
    requirement: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);

  const buyerOptions = [
    'International Buyer',
    'International Dealership Application',
    'Country Distributorship Rights',
    'Importer / Overseas Buyer',
    'Global Distributor',
    'Mall / Shopping Centre Buyer',
    'Multi-Brand Outlet (MBO)',
    'Retailer / Retail Chain',
    'Apparel Brand Partner',
    'Other'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleWhatsAppSubmit = (e) => {
    e.preventDefault();
    const cleanNum = company.whatsapp.replace(/[^0-9]/g, '');
    const message = `Hello RAYYAN DENIM,

CORPORATE ENQUIRY FORM SUBMISSION:
- Full Name: ${formData.fullName}
- Company: ${formData.companyName}
- Country: ${formData.country}
- Email: ${formData.email}
- Phone/WhatsApp: ${formData.phone}
- Buyer Segment: ${formData.buyerType}
- Requirement/Volume: ${formData.requirement}
- Message: ${formData.message}

Please connect with our team. Thank you!`;

    window.open(`https://wa.me/${cleanNum}?text=${encodeURIComponent(message)}`, '_blank');
    setSubmitted(true);
  };

  const handleMailSubmit = (e) => {
    e.preventDefault();
    const subject = `Export Enquiry - ${formData.companyName || formData.fullName} (${formData.country})`;
    const body = `Full Name: ${formData.fullName}
Company Name: ${formData.companyName}
Country: ${formData.country}
Email: ${formData.email}
Phone/WhatsApp: ${formData.phone}
Buyer Segment: ${formData.buyerType}
Requirement / Volume: ${formData.requirement}

Message:
${formData.message}`;

    window.location.href = `mailto:${company.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSubmitted(true);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className="glass-panel"
      style={{ padding: 'clamp(24px, 4vw, 48px)', borderRadius: '16px', position: 'relative' }}
    >
      {submitted ? (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: 'rgba(37, 211, 102, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#25d366', margin: '0 auto 20px' }}>
            <CheckCircle2 size={36} />
          </div>
          <h3 style={{ fontSize: '24px', fontFamily: 'var(--font-heading)', color: 'var(--text-main)', marginBottom: '12px' }}>
            ENQUIRY DISPATCHED!
          </h3>
          <p style={{ color: 'var(--text-muted)', maxWidth: '440px', margin: '0 auto 24px' }}>
            Thank you for reaching out to RAYYAN DENIM. Our export representative will review your corporate requirements shortly.
          </p>
          <button onClick={() => setSubmitted(false)} className="btn-secondary">
            Send Another Enquiry
          </button>
        </div>
      ) : (
        <form onSubmit={handleWhatsAppSubmit}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '20px' }}>
            
            {/* Full Name */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                Full Name *
              </label>
              <input
                type="text"
                name="fullName"
                required
                value={formData.fullName}
                onChange={handleChange}
                placeholder="e.g. Alexander Vance"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  background: '#f8fafc',
                  border: '1px solid var(--color-border)',
                  color: 'var(--text-main)',
                  outline: 'none',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Company Name */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                Company Name *
              </label>
              <input
                type="text"
                name="companyName"
                required
                value={formData.companyName}
                onChange={handleChange}
                placeholder="e.g. Apex Apparel Global"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  background: '#f8fafc',
                  border: '1px solid var(--color-border)',
                  color: 'var(--text-main)',
                  outline: 'none',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Country */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                Country *
              </label>
              <input
                type="text"
                name="country"
                required
                value={formData.country}
                onChange={handleChange}
                placeholder="e.g. Germany / United Arab Emirates"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  background: '#f8fafc',
                  border: '1px solid var(--color-border)',
                  color: 'var(--text-main)',
                  outline: 'none',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Email */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                Corporate Email *
              </label>
              <input
                type="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="alexander@apexapparel.com"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  background: '#f8fafc',
                  border: '1px solid var(--color-border)',
                  color: 'var(--text-main)',
                  outline: 'none',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Phone / WhatsApp */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                Phone / WhatsApp Number *
              </label>
              <input
                type="text"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="+49 151 23456789"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  background: '#f8fafc',
                  border: '1px solid var(--color-border)',
                  color: 'var(--text-main)',
                  outline: 'none',
                  fontSize: '14px'
                }}
              />
            </div>

            {/* Buyer Type */}
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
                Buyer Segment *
              </label>
              <select
                name="buyerType"
                value={formData.buyerType}
                onChange={handleChange}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  borderRadius: '6px',
                  background: '#f8fafc',
                  border: '1px solid var(--color-border)',
                  color: 'var(--text-main)',
                  outline: 'none',
                  fontSize: '14px'
                }}
              >
                {buyerOptions.map((opt) => (
                  <option key={opt} value={opt} style={{ background: '#fff', color: '#0f172a' }}>
                    {opt}
                  </option>
                ))}
              </select>
            </div>

          </div>

          {/* Requirement / Volume */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
              Specific Requirement / Expected Volume
            </label>
            <input
              type="text"
              name="requirement"
              value={formData.requirement}
              onChange={handleChange}
              placeholder="e.g. Slim fit indigo jeans - initial order 5,000 units"
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: '6px',
                background: '#f8fafc',
                border: '1px solid var(--color-border)',
                color: 'var(--text-main)',
                outline: 'none',
                fontSize: '14px'
              }}
            />
          </div>

          {/* Message */}
          <div style={{ marginBottom: '28px' }}>
            <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase' }}>
              Detailed Message / Questions *
            </label>
            <textarea
              name="message"
              required
              rows={4}
              value={formData.message}
              onChange={handleChange}
              placeholder="Provide any specific wash specifications, delivery targets, or sample requests..."
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: '6px',
                background: '#f8fafc',
                border: '1px solid var(--color-border)',
                color: 'var(--text-main)',
                outline: 'none',
                resize: 'vertical',
                fontSize: '14px'
              }}
            />
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit" className="btn-primary" style={{ flexGrow: 1, padding: '16px 28px' }}>
              <MessageSquare size={18} />
              <span>SEND VIA WHATSAPP</span>
            </motion.button>

            <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="button" onClick={handleMailSubmit} className="btn-secondary" style={{ flexGrow: 1, padding: '16px 28px' }}>
              <Mail size={18} />
              <span>SEND VIA EMAIL</span>
            </motion.button>
          </div>

        </form>
      )}
    </motion.div>
  );
};

export default ContactForm;
